import { useSearchParams } from 'react-router-dom'

/* List of search parameters that are supported by this hook.
If any new search parameters are added, they must be added here.
This list is used to ensure type safety when retrieving search parameters. */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SEARCH_PARAMS = [
  'appId',
  'appName',
  'appKey',
  'details',
  'interact',
  'l1',
  'l2',
  'l3',
  'leaf',
  'login',
  'orgId',
  'orgName',
  'projectId',
  'projectName',
  'roles',
  'subscriptionId',
  'tab',
  'teams',
  'code',
  'iss',
  'client_id',
  'authStatus',
  'state',
  'pid',
] as const

export type SearchParamName = (typeof SEARCH_PARAMS)[number]

/**
 * A custom hook that retrieves and parses URL search parameters with type safety.
 *
 * @template T - The expected return type (string or number). Defaults to string if not specified.
 * @param paramName - The name of the search parameter to retrieve. Must be one of the predefined SEARCH_PARAMS.
 * @returns The parameter value as T (string or number) or undefined if it doesn't exist.
 *
 * @example
 * // URL: ?appId=123&name=test
 * const appId = useGetSearchParam<number>('appId') // returns number: 123
 * const name = useGetSearchParam('name') // returns string: "test"
 * const missing = useGetSearchParam('missing') // returns undefined
 */
export function useGetSearchParam<T extends string | number = string>(paramName: SearchParamName): T | undefined {
  const [searchParams] = useSearchParams()
  const paramValue = searchParams.get(paramName)

  if (!paramValue) return undefined

  // Check if the value is a valid integer and T is number
  const num = Number(paramValue)
  if (!isNaN(num) && Number.isInteger(num)) {
    return num as T
  }

  return paramValue as T
}
